import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { CheckboxField, FieldProps, Text, TimeRangeField } from '@frontend/design-system';
import { BusinessProfileFieldTypes } from '../business-preferences/business-profile/config';
import { FormWrapper } from '../form-wrapper';

type Weekday = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

const DAYS: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const ScheduleHours = ({
  getFieldProps,
}: {
  getFieldProps: (
    name: keyof BusinessProfileFieldTypes
  ) => FieldProps<BusinessProfileFieldTypes, keyof BusinessProfileFieldTypes>;
}) => {
  const { t } = useTranslation('ai-receptionist-settings');

  return (
    <FormWrapper.Field>
      <Text weight='bold'>{t('Working Hours')}</Text>
      <div css={styles.mainContainer}>
        {DAYS.map((day) => (
          <AvailabilityHours key={day} day={day} getFieldProps={getFieldProps} />
        ))}
      </div>
    </FormWrapper.Field>
  );
};

const AvailabilityHours = ({ day, getFieldProps }: { day: Weekday; getFieldProps: any }) => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { t } = useTranslation('ai-receptionist-settings');

  return (
    <section key={day} css={{ display: 'flex', flexDirection: 'column' }}>
      <div css={styles.timeRangeFieldRowContainer}>
        <CheckboxField
          label={isMobile ? ShortWeekDayName[day] : WeekDayName[day]}
          className='day-checkbox'
          {...getFieldProps(`${day}-checkbox`)}
        />
        {getFieldProps(`${day}-checkbox`).value ? (
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <div css={{ p: { margin: 0 } }}>
              <TimeRangeField
                label={t('{{day}} Time Range', { day: WeekDayName[day] })}
                joiningText={t('to')}
                {...getFieldProps(`${day}-time-range`)}
                name={`${day}-time-range`}
                css={styles.timeRangeField}
              />
            </div>
          </div>
        ) : (
          <div css={containerStyle}>
            <Icon name='moon-small' />
            <Text>{t('Closed')}</Text>
          </div>
        )}
      </div>
    </section>
  );
};

const styles = {
  mainContainer: css({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowX: 'auto',
  }),

  timeRangeFieldRowContainer: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    minHeight: 60,
    '.day-checkbox': {
      gap: theme.spacing(1),
    },
    '@container (max-width: 700px)': {
      minWidth: 375,
      '.day-checkbox': {
        gap: theme.spacing(0.5),
      },
      '.start-time, .end-time': {
        width: 100,
        input: {
          padding: theme.spacing(0, 1.5),
        },
      },
    },
  }),
  timeRangeField: css({
    gap: theme.spacing(1),
    input: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
    },
    '@container (max-width: 700px)': {
      gap: theme.spacing(0.5),
    },
  }),
  trashIcon: css({
    height: theme.spacing(2.5),
    width: theme.spacing(2.5),
    padding: 0,
  }),
};

const containerStyle = css({
  background: theme.colors.neutral10,
  width: 240,
  maxWidth: 345,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  gap: theme.spacing(1),
  opacity: 0.25,
  '@container (max-width: 700px)': {
    maxWidth: 240,
  },
});

export const WeekDayName: Record<Weekday, string> = {
  sunday: dayjs().set('d', 0).format('dddd'),
  monday: dayjs().set('d', 1).format('dddd'),
  tuesday: dayjs().set('d', 2).format('dddd'),
  wednesday: dayjs().set('d', 3).format('dddd'),
  thursday: dayjs().set('d', 4).format('dddd'),
  friday: dayjs().set('d', 5).format('dddd'),
  saturday: dayjs().set('d', 6).format('dddd'),
};

export const ShortWeekDayName: Record<Weekday, string> = {
  sunday: dayjs().set('d', 0).format('ddd'),
  monday: dayjs().set('d', 1).format('ddd'),
  tuesday: dayjs().set('d', 2).format('ddd'),
  wednesday: dayjs().set('d', 3).format('ddd'),
  thursday: dayjs().set('d', 4).format('ddd'),
  friday: dayjs().set('d', 5).format('ddd'),
  saturday: dayjs().set('d', 6).format('ddd'),
};
